/**
 * Characters that belong to a word, for the purpose of not cutting one in half.
 *
 * Unicode letters and digits, plus the apostrophe and hyphen that live INSIDE
 * screenplay words — "DAVID'S", "walkie-talkie" — because stopping at them
 * would reintroduce the same bug one character further along.
 */
const WORD_CHARACTER = /[\p{L}\p{N}'\u2019-]/u;

/**
 * Grow a selection to whole words.
 *
 * A drag that stops one character short is invisible while making it and
 * permanent afterwards: "AN OLD WOODEN FENC" went onto the sheet as a prop, and
 * nothing downstream could tell it from a deliberate name. The producer is
 * pointing at a phrase, not at a character range, so the phrase is what gets
 * taken — this reads what they meant rather than what their mouse did.
 *
 * Whitespace-only selections come back empty, which the caller treats as no
 * selection at all.
 */
export function snapToWords(text: string, start: number, end: number): string {
  if (text === '') return '';

  let from = Math.max(0, Math.min(start, text.length));
  let to = Math.max(from, Math.min(end, text.length));

  // A drag across a space between two words is not a short selection of either
  // of them — it is not a selection of a phrase at all. Growing it would hand
  // back both neighbours, which is a stranger answer than none.
  if (!WORD_CHARACTER.test(text.slice(from, to))) return '';

  while (from > 0 && WORD_CHARACTER.test(text[from - 1] ?? '')) from -= 1;
  while (to < text.length && WORD_CHARACTER.test(text[to] ?? '')) to += 1;

  return text.slice(from, to).trim();
}
